import React, { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { getBetaStatus, markWelcomeSeen } from "../lib/api";

/**
 * V1.9.0 — One-shot welcome modal for beta testers.
 *
 * Shows on first sign-in (server-side `welcome_seen_at` field is
 * authoritative, with a localStorage fallback so the modal doesn't
 * flash on every page load while /me/beta-status is in flight).
 *
 * Explains the beta context (free credits granted, please report bugs)
 * and links to the feedback button. Single CTA closes the modal AND
 * persists welcome_seen_at on the server.
 */
const LOCAL_KEY = "blueprintai_beta_welcome_seen_v1";

export default function BetaWelcomeModal() {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Cheap localStorage gate — if the user has already dismissed it
    // on this device, never fetch /me/beta-status at all.
    if (localStorage.getItem(LOCAL_KEY) === "1") return;
    (async () => {
      try {
        const r = await getBetaStatus();
        if (cancelled) return;
        setStatus(r.data);
        if (!r.data?.welcome_seen) setOpen(true);
      } catch {
        // Anonymous viewers will get 401 — that's fine, no modal.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDismiss = async () => {
    setClosing(true);
    localStorage.setItem(LOCAL_KEY, "1");
    try {
      await markWelcomeSeen();
    } catch {
      // best-effort; localStorage covers this device anyway.
    }
    setOpen(false);
    setClosing(false);
  };

  if (!open) return null;
  const credits = status?.professional_credits ?? 25;
  return (
    <div
      data-testid="beta-welcome-modal-scrim"
      className="fixed inset-0 z-[60] grid place-items-center bg-zinc-950/90 backdrop-blur-sm p-4"
      onClick={() => !closing && handleDismiss()}
    >
      <div
        data-testid="beta-welcome-modal"
        className="w-full max-w-lg bg-zinc-950 border-2 border-brass-500 shadow-[0_30px_80px_rgba(196,169,107,0.25)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logo-mark-80.png"
              alt="Blueprint AI"
              width={36}
              height={36}
              className="w-9 h-9 rounded-full ring-1 ring-brass-500/60"
              data-testid="beta-welcome-logo-mark"
            />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400">
                // BLUEPRINT_AI · WELCOME
              </div>
              <h2 className="font-display text-xl text-zinc-50 mt-0.5">
                You&apos;re in
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            disabled={closing}
            className="text-zinc-400 hover:text-zinc-100 disabled:opacity-50"
            data-testid="beta-welcome-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-zinc-300 text-sm leading-relaxed">
            Thanks for jumping in! Blueprint AI lets you describe a home in plain English and watch the
            2D floor plan + 3D model build themselves. You're testing the sandbox build — expect rough
            edges.
          </p>

          <div className="border border-brass-700 bg-brass-950/40 px-4 py-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400 mb-1">
              // Free credits granted
            </div>
            <div className="text-brass-100 text-sm">
              <span className="font-display text-2xl mr-1.5">{credits}</span>
              Studio credits added to your account. Use them to unlock construction-quality PDFs and
              AI photo renders — no payment needed during beta.
            </div>
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              // What we'd love from you
            </div>
            <ul className="space-y-1.5 text-zinc-300 text-sm pl-4">
              <li className="list-disc">Try to break things — weird prompts, edge cases, mansions, basements</li>
              <li className="list-disc">Use the brass <span className="text-brass-400">Feedback</span> button (bottom-right) whenever something feels off</li>
              <li className="list-disc">Tell us what's missing — feature requests are welcome too</li>
            </ul>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-zinc-800 flex items-center justify-end">
          <button
            type="button"
            data-testid="beta-welcome-cta"
            onClick={handleDismiss}
            disabled={closing}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-brass-600 hover:bg-brass-500 text-zinc-950 font-mono text-[10px] uppercase tracking-widest disabled:opacity-60"
          >
            Let's go →
          </button>
        </div>
      </div>
    </div>
  );
}
